import React, { useEffect, useMemo, useState } from "react";
import "./UserProfile.css";
import { Settings } from "lucide-react";
import axios from "axios";
import config from "../config";

/* helpers */
function safeParseUserData() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

function readUserKey() {
  const ud = safeParseUserData();
  const key =
    ud?.userKey ??
    ud?.data?.userKey ??
    ud?.user?.userKey ??
    ud?.profile?.userKey ??
    ud?.key ??
    ud?.user_key ??
    "";
  return key ? String(key) : "";
}

const onlyDigits = (v) => (v ? String(v).replace(/\D/g, "") : "");
const normalizeTo91 = (val) => {
  const d = onlyDigits(val);
  if (!d) return "";
  if (d.length === 10) return `91${d}`;
  if (d.length === 12 && d.startsWith("91")) return d;
  return `91${d.slice(-10)}`;
};
const formatIntl91 = (num) =>
  num ? `+${num.slice(0, 2)} ${num.slice(2, 7)} ${num.slice(7)}` : "—";

// generic device name picker
const pickDevName = (d) =>
  d?.deviceName ?? d?.name ?? d?.label ?? d?.devicename ?? "";

/**
 * Helper: read devices from userData in a unified way
 * Supports:
 *   - userData.devices
 *   - userData.flowdevices + userData.packinngdevices (login shape)
 */
function getDevicesFromUserData(ud) {
  if (!ud || typeof ud !== "object") return [];

  // 1) Prefer generic "devices" if present
  if (Array.isArray(ud.devices) && ud.devices.length > 0) {
    return ud.devices;
  }

  // 2) Fall back to login structure
  const flow =
    ud?.flowdevices ||
    ud?.flowDevices ||
    ud?.data?.flowdevices ||
    ud?.data?.flowDevices ||
    [];
  const pack =
    ud?.packinngdevices ||
    ud?.packingdevices ||
    ud?.data?.packinngdevices ||
    ud?.data?.packingdevices ||
    [];

  return [...flow, ...pack];
}

export default function Profile() {
  const [userData, setUserData] = useState(() => safeParseUserData());
  const userKey = useMemo(() => readUserKey(), []);

  const companyName =
    userData.companyName || userData.company || userData.org || "—";

  const [waNumbers, setWaNumbers] = useState([]);
  const [loadingNumbers, setLoadingNumbers] = useState(false);
  const [numbersError, setNumbersError] = useState("");

  const fetchNumbers = async () => {
    if (!userKey) return;
    setLoadingNumbers(true);
    setNumbersError("");
    try {
      const base = config.BASE_URL || config.baseURL || "";
      const res = await axios.get(
        `${base}/api/whatsapp/get-user-whatsapp/${encodeURIComponent(userKey)}`
      );

      const raw = Array.isArray(res?.data?.numbers)
        ? res.data.numbers
        : [];

      const list = raw
        .map((n) => ({
          num91: normalizeTo91(n.whatsappNumber),
          enabled: Boolean(
            n.whatsappEnabled ??
              n.enabled ??
              n.isEnabled ??
              n.receiveData ??
              n.receive_data ??
              true
          ),
          devices: Array.isArray(n.devices) ? n.devices : [],
        }))
        .filter((x) => x.num91);

      setWaNumbers(list);
    } catch (err) {
      setNumbersError("Could not load WhatsApp numbers.");
    } finally {
      setLoadingNumbers(false);
    }
  };

  useEffect(() => {
    fetchNumbers();

    const refresh = () => {
      setUserData(safeParseUserData());
      fetchNumbers();
    };

    window.addEventListener("focus", refresh);
    window.addEventListener("profile:phoneUpdated", refresh);

    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("profile:phoneUpdated", refresh);
    };
  }, [userKey]);

  const goToSettings = () => (window.location.href = "/setting");

  // 🔥 DEVICE NAME ROW (from devices || flowdevices || packinngdevices)
  const deviceNames = useMemo(() => {
    const rawList = getDevicesFromUserData(userData);
    if (!Array.isArray(rawList) || rawList.length === 0) return [];

    const names = rawList
      .map((x) => pickDevName(x))
      .map((s) => String(s || "").trim())
      .filter(Boolean);

    // unique
    return Array.from(new Set(names));
  }, [userData]);

  return (
    <div className="profile-page">
      <div className="profile-header">
        <div className="profile-actions">
          <button className="settings-btn" onClick={goToSettings}>
            <Settings size={18} />
            <span>Settings</span>
          </button>
        </div>
      </div>

      <div className="profile-card">
        {/* COMPANY */}
        <div className="row">
          <div className="label">Company</div>
          <div className="value">{companyName}</div>
        </div>

        {/* WHATSAPP NUMBERS */}
        <div className="row">
          <div className="label">WhatsApp Numbers</div>
          <div className="value" style={{ width: "100%" }}>
            {loadingNumbers ? (
              "Loading…"
            ) : numbersError ? (
              numbersError
            ) : waNumbers.length === 0 ? (
              "—"
            ) : (
              <ol style={{ paddingLeft: "1.25rem" }}>
                {waNumbers.map((x, idx) => (
                  <li key={idx} style={{ marginBottom: "0.6rem" }}>
                    <div
                      className={`wa-line ${x.enabled ? "" : "wa-disabled"}`}
                    >
                      <div className="wa-left">
                        <span className="wa-number">
                          {formatIntl91(x.num91)}
                        </span>
                        {!x.enabled && (
                          <span className="wa-badge">Disabled</span>
                        )}
                      </div>

                      {/* Linked devices – currently ID list */}
                      {x.devices.length > 0 ? (
                        <div className="wa-devices-inline">
                          {x.devices.map((d, i) => (
                            <span key={i} className="wa-chip">
                              {String(d)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="wa-devices-none">No devices</span>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        {/* DEVICE NAME (ALL FROM USERDATA) */}
        <div className="row">
          <div className="label">Device Name</div>
          <div className="value" style={{ width: "100%" }}>
            {deviceNames.length === 0 ? (
              "—"
            ) : (
              <div className="device-list">
                {deviceNames.map((d, i) => (
                  <span key={i} className="device-chip">
                    {"•"} {d}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}